








































































































































































import { Component, Vue } from 'vue-property-decorator'
import { ExStore } from 'vuex'
import BleConnectionManager from '@/ble/connectionManager'
import { BleProviderEventTypes } from '@/ble/constants'
import { BleDeviceTypes } from '@/constants/ble'
import { VitalsignPayload } from '@/api/vitalsign/types'
import { Vitalsign, VitalsignSetting } from '@/types/vitalsign'
import { Patient } from '@/types/user'
import dayjs from 'dayjs'
import ky from 'ky'

type VitelPayload = Partial<
  Pick<
    Vitalsign,
    'spo2' | 'sbp' | 'dbp' | 'pulseRate' | 'temperature' | 'glucose' | 'weight'
  >
>
type VitalKeys = keyof VitelPayload
const DATE_FORMAT = 'YYYY-MM-DD HH:mm'

@Component
export default class ManageRound extends Vue {
  $store!: ExStore
  workersInstitutionCode = ''
  inactivateIds: number[] = []
  selectedId: number | null = null
  patients: Patient[] = []
  vitals: {
    [key: number]: VitalsignPayload
  } = {}
  innnerOrder: { [key: number]: number } = {}
  unsubscribe!: () => void

  get userInfo() {
    return this.$store.getters['user/userInfo']
  }

  get order() {
    return this.innnerOrder
  }

  set order(order: { [key: number]: number }) {
    this.innnerOrder = Object.assign({}, this.innnerOrder, order)
  }

  async created() {
    this.patients = await this.$api.team.fetch()
    const worker = await this.$api.user.worker({ workerId: this.userInfo.id })
    this.workersInstitutionCode = worker.institution

    this.innnerOrder = await this.fetchOrder()
    this.sort()
    this.selectedId = this.patients[0]?.id || null

    const provider = BleConnectionManager.getProvider()
    this.unsubscribe = provider.addListener(
      BleProviderEventTypes.RECEIVE_VALUE,
      async readVal => {
        if (!this.selectedId) return
        switch (readVal.deviceType) {
          case BleDeviceTypes.ELECTRONIC_BLOOD_PRESSURE_MONITOR:
          case BleDeviceTypes.MEDICAL_BLOOD_PRESSURE_MONITOR:
            this.setVital(this.selectedId, 'sbp', readVal.systolicPressure)
            this.setVital(this.selectedId, 'dbp', readVal.diastolicPressure)
            this.setVital(this.selectedId, 'pulseRate', readVal.pr)
            break
          case BleDeviceTypes.ELECTRONIC_THERMOMETER:
          case BleDeviceTypes.NONCONTACT_THERMOMETER:
            this.setVital(this.selectedId, 'temperature', readVal.temperature)
            break
          case BleDeviceTypes.BLOOD_SUGAR_LEVEL_MEASUREMENT:
            this.setVital(this.selectedId, 'glucose', readVal.glucose)
            break
          case BleDeviceTypes.SPO2_MONITOR_MIGHTY_SAT:
          case BleDeviceTypes.SPO2_MONITOR_TM1121:
            this.setVital(this.selectedId, 'spo2', readVal.spo2)
            this.setVital(this.selectedId, 'pulseRate', readVal.pr)
            break
        }
      }
    )
  }

  async beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe()
  }

  async fetchOrder() {
    const order = await this.$api.round.fetch()
    if (!order || !order.length) {
      return {}
    }
    return order.reduce((acc, cur) => {
      if (cur) acc[cur.patient] = cur.seq
      return acc
    }, {} as { [key: number]: number })
  }

  async patchOrder() {
    const payload = Object.entries(this.innnerOrder).map(([patient, seq]) => {
      return {
        patient: Number(patient),
        seq: Number(seq)
      }
    })
    await this.$api.round.patch(payload)
  }

  getPatientLocalId(patientId: number) {
    const patient = this.patients.find(p => p.id == patientId)
    if (!patient || !patient.institutions) return ''
    const mainInstitution = patient.institutions.find(
      i => i.institution === this.workersInstitutionCode
    )
    return mainInstitution ? mainInstitution.patientLocalId : ''
  }

  getVital(id: number, type: VitalKeys) {
    const vital = this.vitals[id]
    if (!vital) return ''
    return vital[type]
  }

  setVital(id: number, type: VitalKeys, value: number) {
    const vital = this.vitals[id] || {}
    vital[type] = value
    this.$set(this.vitals, id, Object.assign({}, vital))
  }

  handleSelectPatient(id: number) {
    this.selectedId = id
  }

  async handleSendVital(id: number, index: number) {
    const vital = this.vitals[id] || null
    if (!vital) return
    if (Object.entries(vital).every(([key, val]) => !val)) return
    const payload = { ...vital, measureDatetime: dayjs().format(DATE_FORMAT) }
    try {
      await this.$api.vitalsign.create({
        patientId: id,
        payload
      })
      this.selectedId = this.patients[++index]?.id || null
      this.inactivateIds.push(id)
    } catch (e) {
      if (e instanceof ky.HTTPError) {
        const errors = await e.response.json()
        this.$store.dispatch('alert/showErrorResponse', errors)
        return
      }
    }
  }

  handleOderChange(id: number, value: number) {
    const { order } = this
    order[id] = value
    this.order = order
  }

  async handleOderFix() {
    this.patchOrder()
    this.sort()
  }

  sort() {
    this.patients = this.patients.sort((a, b) => {
      const oA = this.order[a.id] || Infinity
      const oB = this.order[b.id] || Infinity
      return oA - oB
    })
  }
}
